import { useContext, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Logo from "../../components/Logo/Logo";
import { useNavigate } from "react-router-dom";
import { routes } from "../../shared/router/routes";
import { AuthenticationContext } from "../../shared/providers/authentication/authentication.provider";
import InputText from "../../components/Forms/InputText/InputText";
import { LoadingContext } from "../../shared/providers/loading/loading.provider";
import Success from "./../../shared/assets/imgs/graphic_2.svg";
import {
    IAuthTokens,
    IRegisterValidation,
    IRequestError
} from "../../shared/interfaces/authentication.interface";
import { useAuthLogin, useRegister, useValidateCompanyCode } from "../../shared/hooks/useAuthentication";
import Button from "../../components/Button/Button";
import { TranslationContext } from "../../shared/providers/translation/translation.provider";
import { AnalyticsContext } from "../../shared/providers/analytics/analytics.provider";
import { ICompany } from "../../shared/interfaces/company.interface";
import { formatTemplate } from "../../shared/helpers/utils";
import Select from "../../components/Forms/Select/Select";
import { JOB_TITLES } from "../../shared/constants";
import Link from "../../components/Link/Link";

function Register() {
    const { isLogout, setIsLogout } = useContext(AuthenticationContext);
    const { setIsLoading } = useContext(LoadingContext);
    const { translate } = useContext(TranslationContext);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [company, setCompany] = useState<ICompany>();
    const [confirm, setConfirm] = useState<boolean>(false);

    const validateCode = useValidateCompanyCode();

    const form = useFormik({
        initialValues: {
            companyCode: ""
        },
        validationSchema: Yup.object().shape({
            companyCode: Yup.string().required(translate("common.companyCode.required", "Company code is required")),
        }),
        validateOnChange: false,
        onSubmit: async (values: IRegisterValidation) => {
            setIsLoading(true);
            const response = await validateCode.mutateAsync(values.companyCode);

            if (response.success) {
                setCompany(response.data as ICompany);
            } else {
                form.setErrors({
                    companyCode: translate("common.companyCode.invalid", "Company code is invalid")
                })
            }
            setIsLoading(false);
        }
    });

    useEffect(() => {
        return () => {
            setIsLogout(false);
            setCompany(undefined);
            setConfirm(false);
        }
    }, []);

    return (
        <>
            <Logo className="d-none d-md-block" />
            <div className="d-flex flex-column gap-22 gap-md-2 w-100">
                {!company && <>
                    <h1 className="d-md-none text-h1-big-bd login_title">
                        {translate("page.register.title", "Register")}
                    </h1>
                    <h1 className="d-none d-md-block text-h1-big-bd login_title w-100 text-md-center">
                        {translate("page.register.title", "Register")}
                    </h1>
                    <form className="form d-flex flex-column gap-12" onSubmit={form.handleSubmit}>
                        <InputText
                            name="companyCode"
                            inputMode="text"
                            label={translate("common.companyCode", "Company code")}
                            placeholder={translate("form.placeholder.companyCode", "Insert company code")}
                            type="text"
                            errors={form.submitCount > 0 ? form.errors.companyCode?.split("#") : []}
                            onChange={form.handleChange}
                        />

                        <Button
                            type="submit"
                            variant="solid"
                            customCss="btn_action mt-02 align-self-center"
                            label={translate("common.validate", "Validate")}
                        />
                    </form>
                </>}

                {company && !confirm && <>
                    <h1 className="text-h1-big-bd login_title text-center">
                        {company.name}
                    </h1>
                    <h2 className="text-h2-bd login_title text-center">
                        {formatTemplate(
                            translate(
                                "page.register.confirm_company",
                                "If this is your company, click confirm."
                            ),
                            [company.name]
                        )}
                    </h2>
                    <Button
                        type="submit"
                        variant="solid"
                        onClick={() => setConfirm(true)}
                        customCss="btn_action mt-02 align-self-center"
                        label={translate("common.confirm", "Confirm")}
                    />
                </>}

                {confirm && <RegisterForm code={form.values.companyCode} />}
            </div >
        </>
    );
}

function RegisterForm({ code }: { code: string }) {
    const { translate } = useContext(TranslationContext);
    const { setIsLoading } = useContext(LoadingContext);
    const [isSuccess, setIsSuccess] = useState<boolean>();
    const register = useRegister();

    const form = useFormik({
        initialValues: {
            username: "",
            email: "",
            jobTitle: ""
        },
        validationSchema: Yup.object().shape({
            username: Yup.string().required(translate("common.username.required", "Username is required")),
            email: Yup.string()
                .email(translate("common.email.invalid", "Insert a valid email please"))
                .required(translate("common.email.required", "Email is required")),
            jobTitle: Yup.string().required(translate("common.jobTitle.required", "Job title is required")),
        }),
        validateOnChange: false,
        onSubmit: async (values: {
            username: string;
            email: string;
            jobTitle: string;
        }) => {
            setIsLoading(true);

            const response = await register.mutateAsync({
                hrsInviteCode: code,
                firstName: values.username,
                lastName: "",
                ...values
            });

            console.log(response);

            setIsSuccess(response.success);
            setIsLoading(false);
        }
    });

    return <>
        {isSuccess === undefined && <>
            <h1 className="d-md-none text-h1-big-bd login_title">
                {translate("page.register.complete_register", "Complete your registration")}
            </h1>
            <h1 className="d-none d-md-block text-h1-big-bd login_title w-100 text-md-center">
                {translate("page.register.complete_register", "Complete your registration")}
            </h1>
            <form className="form d-flex flex-column gap-12" onSubmit={form.handleSubmit}>

                <InputText
                    name="username"
                    inputMode="text"
                    label={translate("common.username", "Username")}
                    placeholder={translate("form.placeholder.username", "Insert username")}
                    type="text"
                    errors={form.submitCount > 0 ? form.errors.username?.split("#") : []}
                    onChange={form.handleChange}
                />
                <InputText
                    name="email"
                    inputMode="text"
                    label={translate("common.email", "Email")}
                    placeholder={translate("form.placeholder.email", "Insert email")}
                    type="text"
                    errors={form.submitCount > 0 ? form.errors.email?.split("#") : []}
                    onChange={form.handleChange}
                />

                <Select
                    value={[form.values.jobTitle]}
                    onChange={([option]) => form.setFieldValue("jobTitle", option?.value)}
                    label={translate("common.jobTitle", "Job title")}
                    placeholder={translate("form.placeholder.select_option", "Select option")}
                    options={Object.entries(JOB_TITLES).map(([key, value]) => ({
                        label: translate(`jobtitle.${key}`, key),
                        value
                    }))}
                    errors={
                        form.submitCount > 0
                            ? form.errors.jobTitle?.split("/")
                            : []
                    }
                />

                <Button
                    type="submit"
                    variant="solid"
                    customCss="btn_action mt-02 align-self-center"
                    label={translate("common.register", "Register")}
                />
            </form></>}

        {!isSuccess && isSuccess !== undefined && (
            <div className="login_message d-flex flex-column justify-content-center align-items-center text-center gap-1">
                <img src={Success} alt="" aria-hidden="true" className="mb-2" />
                <h1 className="title text-h1-md px-md-5 mx-5">
                    {translate("common.register.error", "There was an error with your registration")}
                </h1>
                <span className="text-bd1-rg message">
                    {translate("common.register.retry", "Validate your data.")}
                </span>
                <button
                    onClick={() => setIsSuccess(undefined)}
                    className="button bg-green-500 color-neutrals-black text-bd2-bd w-100 mt-1">
                    {translate("common.back", "Back")}
                </button>
            </div>
        )}

        {isSuccess && (
            <div className="login_message d-flex flex-column justify-content-center align-items-center text-center gap-1">
                <img src={Success} alt="" aria-hidden="true" className="mb-2" />
                <h1 className="text-h1-md title px-md-5 mx-md-5">
                    {translate("page.register.step_1_success", "Check your email and confirm your registration")}
                </h1>
            </div>
        )}
    </>
}

export default Register;
